import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-icon-button.css?inline';

/**
 * `<pk-icon-button>` — square button for icon-only actions (close, menu, etc.).
 * See `docs/components/pk-icon-button.md`.
 *
 * Slots:
 *   - default — an SVG icon
 *
 * Attributes:
 *   - `label`   (REQUIRED) — becomes `aria-label` on the inner button; the only
 *                            accessible name since there is no visible text
 *   - `variant` — `ghost` (default) | `solid` | `danger`
 *   - `loading` — convenience boolean; sets `data-submitting="true"`
 *   - `data-submitting`       — `"true"` while busy; sets `aria-busy`, dims opacity
 *   - `data-submitting-label` — `aria-label` override while loading (default: `label + " (loading)"`)
 *   - `disabled`              — forwarded to the inner `<button>`
 */
export class PkIconButton extends HTMLElement {
    static readonly tagName = 'pk-icon-button';

    static get observedAttributes(): string[] {
        return ['label', 'variant', 'loading', 'data-submitting', 'data-submitting-label', 'disabled'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('click', this._handleClick.bind(this));
        this.render();
    }

    attributeChangedCallback(name: string): void {
        if (name === 'loading') {
            if (this.hasAttribute('loading')) {
                this.dataset.submitting = 'true';
            } else {
                delete this.dataset.submitting;
            }
        }
        this.render();
    }

    private _handleClick(e: Event): void {
        const isSubmitting = this.dataset.submitting === 'true';
        const isDisabled   = this.hasAttribute('disabled');
        if (isSubmitting || isDisabled) {
            e.stopPropagation();
        }
    }

    private get _isSubmitting(): boolean {
        return this.dataset.submitting === 'true';
    }

    private template(): TemplateResult {
        const label       = this.getAttribute('label') ?? '';
        const variant     = this.getAttribute('variant') ?? 'ghost';
        const isSubmitting = this._isSubmitting;
        const isDisabled  = this.hasAttribute('disabled');
        const submittingLabel = this.dataset.submittingLabel ?? `${label} (loading)`;
        const ariaLabel   = isSubmitting ? submittingLabel : label;

        return html`
            <style>${styles}</style>
            <button
                class="btn btn--${variant}"
                aria-label="${ariaLabel}"
                aria-busy="${isSubmitting ? 'true' : 'false'}"
                ?disabled="${isDisabled || isSubmitting}"
                data-submitting="${isSubmitting ? 'true' : 'false'}"
            >
                <slot></slot>
            </button>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkIconButton.tagName)) {
    customElements.define(PkIconButton.tagName, PkIconButton);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-icon-button': PkIconButton;
    }
}
